/**
 * A RULE SAVED WITH AN OLD CONDITION READS AS THE NEW ONES (MTR #397).
 *
 * The Product Owner, 2026-10-05: a condition is one fact, and a rule puts facts together with "and".
 * Three conditions said two things at once and were split. A rule already saved with one of them is
 * read here as the conditions it meant, so nobody has to redo a rule, and saving it again writes the
 * new ones. Nothing in the database is rewritten.
 *
 * Only an old condition whose meaning the new ones say EXACTLY, with "and", is translated. "Not only
 * missed calls" would need "or" (a call with words, or no missed call at all), and "Her own email is on
 * a booking of hers" has no exact one-fact twin (her email on the lead hides the booking's), so those
 * stay as they were saved — still read, still working, just never offered again (review of #397).
 */
const ONE = (field, op, value) => ({ field, op, value })

/*
  `NEW` AND `IN_CONVERSATION` → `INQUIRY`, PLUS THE QUESTION THE STAGE USED TO ANSWER (#554 step C).

  The two stages became one, and a rule somebody saved against the old pair must keep meaning what
  they chose. What it meant is not just the stage:

    NEW               "it is an enquiry AND nobody here has answered her yet"
    IN_CONVERSATION   "it is an enquiry AND somebody has"
    both of them      "it is an enquiry" — nothing else, so nothing is added

  THE SECOND HALF IS WHY THIS EXISTS AND IS NOT A TIDY-UP. One saved rule on production
  ("First reply — ask Zillow / Furnished Finder for her phone", off at the time of writing) is
  filtered on `Lead status is one of NEW` and its action SENDS A TEXT TO THE RENTER. Translated as
  the stage alone it would read "Lead status is Inquiry" and, the day somebody switched it on, text
  all 51 renters — including the ones we are mid-conversation with. `neverReplied` ("No reply from
  us yet") was added in #554 ahead of this step for exactly this, and it asks the timeline rather
  than the stage, which is the whole point of the merge.

  ONLY `IS_ONE_OF` IS TRANSLATED THIS WAY. "Is not one of New" means "not (an enquiry AND nobody
  answered)", which needs an "or" — and this file only ever translates what the new conditions say
  EXACTLY, with "and". The value is still renamed, so such a rule reads "is not one of Inquiry": a
  wider exclusion than before, which makes the rule fire for FEWER leads rather than more. A rule
  that does too little is noticed by whoever wanted it; a rule that does too much texts people.

  Nothing in the database is rewritten by this; saving the rule again writes the new conditions.
*/
const INQUIRY_WAS = { NEW: 'INQUIRY', IN_CONVERSATION: 'INQUIRY' }
/*
  `WON` → `CONFIRMED` AND `LOST` → `NOT_WON`: PLAIN RENAMES (the Product Owner, 2026-11-03: the old stage words
  are gone and nobody knows what they mean any more). Unlike the pair above, each is one stage that became one
  stage, so nothing is added: a rule saved on "Lead status is one of WON" means "is one of Confirmed". Without
  this it matched nothing at all, silently (MTR #809).
*/
const RENAMED = { ...INQUIRY_WAS, WON: 'CONFIRMED', LOST: 'NOT_WON' }
const renameStages = (values) => {
  const out = []
  for (const v of values) {
    const next = RENAMED[v] ?? v
    if (!out.includes(next)) out.push(next)
  }
  return out
}

function leadStageUpgrade(f) {
  const values = Array.isArray(f.value) ? f.value : [f.value]
  if (!values.some((v) => v in RENAMED)) return null
  const renamed = ONE('leadStage', f.op, renameStages(values))
  if (f.op !== 'IS_ONE_OF' || !values.some((v) => v in INQUIRY_WAS)) return [renamed]
  const hadNew = values.includes('NEW')
  const hadTalking = values.includes('IN_CONVERSATION')
  if (hadNew && hadTalking) return [renamed]
  return [renamed, ONE('neverReplied', hadNew ? 'IS_TRUE' : 'IS_FALSE', '')]
}

const UPGRADE = {
  hasOwnEmail: (f) => (f.op === 'IS_TRUE' ? [ONE('leadEmail', 'IS_ONE_OF', ['OWN'])]
    : f.op === 'IS_FALSE' ? [ONE('leadEmail', 'IS_ONE_OF', ['RELAY', 'NONE'])] : null),
  detectionOnlyMissedCalls: (f) => (f.op === 'IS_TRUE'
    ? [ONE('detectionMissedCall', 'IS_TRUE', ''), ONE('detectionSaidSomething', 'IS_FALSE', '')] : null),
  leadStage: leadStageUpgrade,
}

export function upgradeFilters(filters) {
  const out = []
  for (const f of filters ?? []) {
    const next = UPGRADE[f?.field]?.(f)
    if (!next) { out.push(f); continue }
    const same = (a, b) => a.field === b.field && a.op === b.op && JSON.stringify(a.value) === JSON.stringify(b.value)
    for (const n of next) if (!out.some((o) => same(o, n))) out.push(n)
  }
  return out
}
